import { createSelector, PayloadAction } from '@reduxjs/toolkit';
import { createExtendedSlice } from '../createExtendedSlice';
import { AppReducerState } from './types';
import { GoogleUserInfo } from '../services/types';
import { RootState } from '../../../store';
import { LogMsgFailed } from '../routingSlice/types';

const sliceName = 'appView';

// 4 state statuses (assets / snapshot):
// 1. undefined === initial
// 2. null === fetching
// 3. empty array === no data
// 4. array with items === with data
const initialState: AppReducerState = {
  user: null,
  isDrawerOpen: null,
  isUserMenuOpen: null,
};
// =============================
// ------- Slice ----------
// =============================
export const appViewSlice = createExtendedSlice({
  name: sliceName,
  initialState,
  reducers: {
    // ---- Internal ---
    appViewFetchUserRequest: (state, { payload }: PayloadAction<string>) => {},
    appViewFetchUserSuccess: (
      state,
      { payload }: PayloadAction<GoogleUserInfo>,
    ) => {
      const { picture, family_name, given_name, email } = payload;
      state.user = {
        image: picture,
        first_name: given_name,
        last_name: family_name,
        email,
      };
    },
    appViewFetchUserFailed: (
      state,
      { payload }: PayloadAction<LogMsgFailed>,
    ) => {},
    appViewSetUser: (
      state,
      { payload }: PayloadAction<AppReducerState['user']>,
    ) => {
      state.user = payload;
    },
    appViewUpdateIsDrawerOpen: (
      state,
      { payload }: PayloadAction<boolean | undefined>,
    ) => {
      const nextIsOpen =
        payload ?? state.isDrawerOpen === null ? false : !state.isDrawerOpen;
      state.isDrawerOpen = nextIsOpen;
    },
    appViewUpdateIsUserMenuOpen: (
      state,
      { payload }: PayloadAction<boolean | undefined>,
    ) => {
      const nextIsOpen =
        payload ?? state.isUserMenuOpen === null ? true : !state.isUserMenuOpen;
      state.isUserMenuOpen = nextIsOpen;
    },
  },
});

// =============================
// ------- selectors ----------
// =============================
export const getAppViewUser = (state: RootState) => state.appView.user;
// TODO
export const getAppViewUserInboxesCount = (state: RootState) => 1;
export const getAppViewIsDrawerOpen = (state: RootState) =>
  state.appView.isDrawerOpen;
export const getAppViewIsUserMenuOpen = (state: RootState) =>
  state.appView.isUserMenuOpen;

export const getAppViewUserFirstName = createSelector(
  getAppViewUser,
  (user) => {
    return user?.first_name ?? null;
  },
);
export const getAppViewUserProfileImage = createSelector(
  getAppViewUser,
  (user) => {
    return user?.image ?? null;
  },
);

export const getAppViewUserName = createSelector(getAppViewUser, (user) => {
  if (!user) return null;
  return `${user.first_name ? user.first_name : ''}${user.last_name ? ` ${user.last_name}` : ''}`;
});
export const getAppViewRelevantIsDrawerOpen = createSelector(
  getAppViewIsDrawerOpen,
  // (_: any, isOpen: boolean) => isOpen,
  (isDrawerOpen) => {
    if (isDrawerOpen === null) return true;

    return isDrawerOpen;
  },
);
export const getAppViewRelevantIsUserMenuOpen = createSelector(
  getAppViewIsUserMenuOpen,
  (isUserMenuOpen) => {
    if (isUserMenuOpen === null) return false;

    return isUserMenuOpen;
  },
);
// =============================
// ------- Exports ----------
// =============================

export const {
  appViewFetchUserRequest,
  appViewFetchUserSuccess,
  appViewFetchUserFailed,
  appViewSetUser,
  appViewUpdateIsDrawerOpen,
  appViewUpdateIsUserMenuOpen,
} = appViewSlice.actions;

export default appViewSlice.reducer;
